import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {SelectField} from '@/components/forms';
import {ChartQuery} from 'entities/chart';
import {FormikT} from 'utils/types';
import {CustomizeFormField} from 'entities/chartFilters';
import ChartQueryEditor from '@/views/storyboard/chart_settings/ChartQueryEditor';
import EventFiltersBundle from '@/components/event_filters/EventFiltersBundle';
import ErrorBoundary from '@/views/storyboard/cell/ErrorBoundary';
import i18n from 'i18n';

const QUERY_SYNC_DEBOUNCE_MS = 500;

const selectStyles = {
    control: (base: any) => ({...base, minHeight: 34, height: 34}),
    valueContainer: (base: any) => ({...base, padding: '0 8px'}),
    indicatorsContainer: (base: any) => ({...base, height: 34}),
    option: (base: any, state: any) => ({
        ...base,
        background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        ':active': {
            ...base[':active'],
            backgroundColor: 'rgba(0, 158, 251, 0.08)',
        },
    }),
};

interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    customizeFormFieldChoices: CustomizeFormField[];
}

export interface ChartSettingsQueryPanelRef {
    flushPendingQuery: () => void;
}

const chartQueryWithoutSearchText = (query: ChartQuery): Omit<ChartQuery, 'query_string'> => {
    const {query_string: _queryString, ...rest} = query;
    return rest;
};

const ChartSettingsQueryPanel = React.memo(forwardRef<ChartSettingsQueryPanelRef, Props>(({
    chartQueryFormik,
    customizeFormFieldChoices,
}, ref) => {
    const formikQuery = chartQueryFormik.values.query_string || '';
    const [localQuery, setLocalQuery] = useState(formikQuery);
    const localQueryRef = useRef(localQuery);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const skipExternalSyncRef = useRef(false);

    localQueryRef.current = localQuery;

    useEffect(() => {
        if (skipExternalSyncRef.current) {
            skipExternalSyncRef.current = false;
            return;
        }

        if (formikQuery !== localQueryRef.current) {
            setLocalQuery(formikQuery);
        }
    }, [formikQuery]);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    const syncQueryToFormik = useCallback((value: string) => {
        if (value === chartQueryFormik.values.query_string) {
            return;
        }

        skipExternalSyncRef.current = true;
        chartQueryFormik.setFieldValue('query_string', value, false);
    }, [chartQueryFormik]);

    const flushPendingQuery = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        syncQueryToFormik(localQueryRef.current);
    }, [syncQueryToFormik]);

    useImperativeHandle(ref, () => ({
        flushPendingQuery,
    }), [flushPendingQuery]);

    const handleQueryChange = useCallback((value: string) => {
        setLocalQuery(value);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            syncQueryToFormik(value);
        }, QUERY_SYNC_DEBOUNCE_MS);
    }, [syncQueryToFormik]);

    const handleQueryBlur = useCallback(() => {
        flushPendingQuery();
    }, [flushPendingQuery]);

    return (
        <>
            <div className="chart-settings-form-query-section">
                <div className="chart-settings-form-section__title chart-settings-form-section__title--numbered">
                    <span className="chart-settings-form-section__number">1</span>
                    {i18n.t('cs_section_data.text')}
                </div>

                <div className="chart-settings-query-card">
                    <div className="cs-field">
                        <ChartQueryEditor
                            value={localQuery}
                            onChange={handleQueryChange}
                            onBlur={handleQueryBlur}
                            error={typeof chartQueryFormik.errors.query_string === 'string' ? chartQueryFormik.errors.query_string : undefined}
                            isInvalid={!!chartQueryFormik.errors.query_string && !!chartQueryFormik.touched.query_string}
                        />
                    </div>

                    <div className="cs-field">
                        <label className="cs-field__label">{i18n.t('jt_field_query.text')}</label>
                        <SelectField
                            compact
                            name="fields_to_query"
                            value={customizeFormFieldChoices.find(el => el[0] === chartQueryFormik.values.fields_to_query) ? {
                                value: chartQueryFormik.values.fields_to_query,
                                label: customizeFormFieldChoices.find(el => el[0] === chartQueryFormik.values.fields_to_query)?.[1] || ''
                            } : null}
                            onChange={(v) => chartQueryFormik.setFieldValue('fields_to_query', v?.value || customizeFormFieldChoices[0][0])}
                            options={customizeFormFieldChoices.map((el) => ({
                                value: el[0],
                                label: el[1],
                            }))}
                            isRequired={false}
                            error={chartQueryFormik.errors.fields_to_query}
                            touched={chartQueryFormik.touched.fields_to_query}
                            customStyles={selectStyles}
                        />
                    </div>
                </div>

                <div className="cs-field chart-settings-form-filters-section chart-settings-query-card">
                    <ErrorBoundary>
                        <EventFiltersBundle<ChartQuery>
                            formValues={chartQueryFormik.values}
                            setValues={chartQueryFormik.setValues}
                            setFieldValue={chartQueryFormik.setFieldValue}
                            parent="chart-settings-form"
                            oneFilterColSize={12}
                            enableCollapse={false}
                            pickerLayout="chart-settings"
                        />
                    </ErrorBoundary>
                </div>
            </div>
        </>
    );
}), (prevProps, nextProps) => (
    prevProps.customizeFormFieldChoices === nextProps.customizeFormFieldChoices
    && isEqual(
        chartQueryWithoutSearchText(prevProps.chartQueryFormik.values),
        chartQueryWithoutSearchText(nextProps.chartQueryFormik.values),
    )
    && prevProps.chartQueryFormik.errors === nextProps.chartQueryFormik.errors
    && prevProps.chartQueryFormik.touched === nextProps.chartQueryFormik.touched
));

export default ChartSettingsQueryPanel;
